import { useState } from "react";

const ROLLING = [7, 30, 90];
// Indian financial year runs April to March; each entry is [calendar month number, short name].
const FY_MONTHS = [[4, "Apr"], [5, "May"], [6, "Jun"], [7, "Jul"], [8, "Aug"], [9, "Sep"], [10, "Oct"], [11, "Nov"], [12, "Dec"], [1, "Jan"], [2, "Feb"], [3, "Mar"]];

// One reporting period shared by the Production & Operations and Sales dashboards.
//   fy null            -> rolling: the last `days` days
//   fy set, month null -> that whole financial year (fy = the year it starts in, 2026 for FY 2026-27)
//   fy and month set   -> one calendar month inside that year
export function usePeriod(initialDays = 30) {
  const [days, setDays] = useState(initialDays);
  const [fy, setFy] = useState(null);
  const [month, setMonth] = useState(null);
  const params = fy == null ? { days } : month == null ? { fy } : { fy, month };
  return { days, fy, month, params, setDays, setFy, setMonth };
}

export function RollingPeriod({ period }) {
  const { days, fy, setDays, setFy, setMonth } = period;
  return (
    <div className="inline-flex rounded-full bg-white/10 p-1 ring-1 ring-white/10" role="group" aria-label="Rolling period">
      {ROLLING.map((p) => (
        <button key={p} onClick={() => { setFy(null); setMonth(null); setDays(p); }} data-testid={`period-${p}`}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${fy == null && days === p ? "bg-white text-slate-900" : "text-slate-300 hover:text-white"}`}>
          {p} days
        </button>
      ))}
    </div>
  );
}

// `data` is the API response: it must carry period {fy, label, prev_label}, as_of and fy_options.
export function FinancialYearBar({ period, data, note }) {
  const { fy, month, setFy, setMonth } = period;
  const base = fy ?? data.period.fy;
  const asOf = new Date(`${data.as_of}T00:00:00`);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="period-fy">
      <span className="text-[11px] uppercase tracking-wider text-slate-400">Financial year</span>
      <select value={fy ?? ""} onChange={(e) => { setMonth(null); setFy(e.target.value === "" ? null : Number(e.target.value)); }}
              data-testid="period-fy-select" aria-label="Financial year"
              className="h-8 rounded-full border-0 bg-white/10 px-3 text-xs font-semibold text-white ring-1 ring-white/15 [color-scheme:dark]">
        <option value="">Rolling</option>
        {data.fy_options.map((o) => <option key={o.fy} value={o.fy}>{o.label}</option>)}
      </select>
      <div className="flex flex-wrap gap-1" role="group" aria-label="Month">
        <button onClick={() => setMonth(null)} disabled={fy == null} data-testid="period-month-all"
                className={`rounded-full px-3 py-1 text-xs font-semibold transition disabled:opacity-30 ${fy != null && month == null ? "bg-white text-slate-900" : "bg-white/10 text-slate-300 hover:text-white"}`}>
          Full year
        </button>
        {FY_MONTHS.map(([m, name]) => {
          const first = new Date(m >= 4 ? base : base + 1, m - 1, 1);
          return (
            <button key={m} onClick={() => { if (fy == null) setFy(data.period.fy); setMonth(m); }} disabled={first > asOf} data-testid={`period-month-${m}`}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition disabled:opacity-30 ${fy != null && month === m ? "bg-white text-slate-900" : "bg-white/10 text-slate-300 hover:text-white"}`}>
              {name}
            </button>
          );
        })}
      </div>
      <span className="ml-auto text-[11px] text-slate-400">
        Showing {data.period.label}, compared with {data.period.prev_label}.{note ? ` ${note}` : ""}
      </span>
    </div>
  );
}
